import React from 'react';
import { Modal, Switch } from '@mantine/core';
import classes from './pos-settings-modal.module.scss';

type PosSettingsModalProps = {
  opened: boolean;
  onClose: () => void;
  viewportWidth: number;
  isSubmitting: boolean;
  isSuperAdmin: boolean;
  requiresManagerApproval: boolean;
  onChangeRequiresManagerApproval: (next: boolean) => void;
  ordersPageWaiterOwnVisibility: boolean;
  onChangeOrdersPageWaiterOwnVisibility: (next: boolean) => void;
  manualCreditEntryBlocked: boolean;
  onChangeManualCreditEntryBlocked: (next: boolean) => void;
  refundCodeViaPush: boolean;
  onChangeRefundCodeViaPush: (next: boolean) => void;
  rootClassName?: string;
};

export const PosSettingsModal: React.FC<PosSettingsModalProps> = ({
  opened,
  onClose,
  viewportWidth,
  isSubmitting,
  isSuperAdmin,
  requiresManagerApproval,
  onChangeRequiresManagerApproval,
  ordersPageWaiterOwnVisibility,
  onChangeOrdersPageWaiterOwnVisibility,
  manualCreditEntryBlocked,
  onChangeManualCreditEntryBlocked,
  refundCodeViaPush,
  onChangeRefundCodeViaPush,
  rootClassName,
}) => {
  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="הגדרות POS"
      closeButtonProps={{ 'data-testid': 'settings-pos-config-close', disabled: isSubmitting } as Record<string, unknown>}
      size={viewportWidth >= 768 ? 'clamp(760px, 78vw, 1120px)' : 'md'}
      centered={viewportWidth >= 768}
      radius="md"
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
      classNames={{
        root: rootClassName,
        content: classes.modalContent,
        body: classes.modalViewport,
      }}
    >
      <div className={classes.modalBody} dir="rtl">
        <p className={classes.lead}>הגדרות אלה נשמרות ברמת המסעדה.</p>
        <div className={classes.list}>
          <label className={classes.row} htmlFor="pos-setting-general-item-approval">
            <div className={classes.textBlock}>
              <span className={classes.label}>פריט כללי באישור מנהל</span>
              <span className={classes.hint}>כאשר כבוי, עובד שאינו מנהל מוסיף פריט כללי בלי המתנה לאישור מנהל.</span>
            </div>
            <Switch
              id="pos-setting-general-item-approval"
              checked={requiresManagerApproval}
              onChange={(event) => {
                onChangeRequiresManagerApproval(event.currentTarget.checked);
              }}
              disabled={isSubmitting}
              data-testid="pos-setting-general-item-approval"
            />
          </label>
          <label className={classes.row} htmlFor="pos-setting-orders-page-waiter-own-visibility">
            <div className={classes.textBlock}>
              <span className={classes.label}>דף הזמנות: עובד רואה רק הזמנות שהוא פתח</span>
              <span className={classes.hint}>כאשר פעיל, עובדים שאינם מנהלים רואים ברשימת ההזמנות רק הזמנות שהאש הראשונה שלהן בוצעה על ידם.</span>
            </div>
            <Switch
              id="pos-setting-orders-page-waiter-own-visibility"
              checked={ordersPageWaiterOwnVisibility}
              onChange={(event) => {
                onChangeOrdersPageWaiterOwnVisibility(event.currentTarget.checked);
              }}
              disabled={isSubmitting}
              data-testid="pos-setting-orders-page-waiter-own-visibility"
            />
          </label>
          <label className={classes.row} htmlFor="pos-setting-manual-credit-entry-blocked">
            <div className={classes.textBlock}>
              <span className={classes.label}>חסימת אשראי ידני</span>
              <span className={classes.hint}>כאשר פעיל, אפשרות הזנת פרטי כרטיס ידנית מוסתרת במסך התשלום.</span>
            </div>
            <Switch
              id="pos-setting-manual-credit-entry-blocked"
              checked={manualCreditEntryBlocked}
              onChange={(event) => {
                onChangeManualCreditEntryBlocked(event.currentTarget.checked);
              }}
              disabled={isSubmitting}
              data-testid="pos-setting-manual-credit-entry-blocked"
            />
          </label>
          {isSuperAdmin && (
            <label className={classes.row} htmlFor="pos-setting-refund-code-via-push">
              <div className={classes.textBlock}>
                <span className={classes.label}>קוד זיכוי דרך התראות push</span>
                <span className={classes.hint}>כאשר פעיל, קוד אימות זיכוי נשלח בהתראת PWA במקום במייל.</span>
              </div>
              <Switch
                id="pos-setting-refund-code-via-push"
                checked={refundCodeViaPush}
                onChange={(event) => {
                  onChangeRefundCodeViaPush(event.currentTarget.checked);
                }}
                disabled={isSubmitting}
                data-testid="pos-setting-refund-code-via-push"
              />
            </label>
          )}
        </div>
      </div>
    </Modal>
  );
};
